import _                                              from 'underscore';
import React, { FC, useContext, useEffect, useState } from 'react';
import { ProjectContext, ProjectContextType }         from './ProjectIndexPage';
import { useQuery }                                   from '@apollo/react-hooks';

import { runIfFunction }                   from '@src/util';
import { queriesHaveData, queriesLoading } from '@src/store/_apolloUtil';
import { ProjectQueries }                  from '@src/queries/ProjectQueries';
import { CorvalProject, Uuid }             from '@src/types';
import { ITermDictionary }                 from '@src/lang/LangTypes';
import { usePrefStates }                   from "@src/hooks";
import { ProjectService }                  from "@src/services";


export const EMPTY_PROJECT_CONFIG = '{}';

export type ProjectContextConfigType =
    Pick<ProjectContextType,
        'isDiscoveryComplete' |
        'isCorvalManaged' |
        'isTemplateSource' |
        'projectUtilSettings' |
        'projectBusinessFunctions' |
        'projectDictionary'
    > & { projectVersionId?: Uuid }

interface Props {

    // Callback method once config is acquired
    onConfigUpdated?: ( config: ProjectContextConfigType ) => void;
}

const defaultProps: Props = {

    onConfigUpdated: ( config ) => {}
};

const ProjectConfig: FC<Props> = ( props ) => {

    let { projectId }                         = useContext( ProjectContext ),
        [ config, setConfig ]                 = useState( null ),
        [ , projectSpecificPrefs, prefEnums ] = usePrefStates( projectId );

    // Get project specific settings and dictionary
    let getProjectConfigReq = useQuery<{ projects: CorvalProject[] }>( ProjectQueries.queries.GET_PROJECT( ProjectQueries.fragments.PROJECTS.CONFIG_FIELDS ), {
        variables:   { id: projectId },
        fetchPolicy: 'no-cache',
    } );

    // Get project specific settings and dictionary
    let getProjectTemplatesReq = useQuery( ProjectQueries.queries.GET_PROJECT_TEMPLATES(), {
        fetchPolicy: 'no-cache',
    } );


    // When settings request changes...
    useEffect( () => {

        if( !queriesLoading( [ getProjectConfigReq, getProjectTemplatesReq ] ) &&
            queriesHaveData( [ getProjectConfigReq, getProjectTemplatesReq ] ) ){

            let project = _.first( getProjectConfigReq.data.projects ) ?? null;

            if( project ){

                console.log( 'Assessing project config...' );
                console.log( { projectId } );

                let projectVersionId = projectSpecificPrefs.get( prefEnums.PROJECT_VERSION_ID );

                const projectVersionNotFound = !_.pluck( project.projectVersions, 'id' ).includes( projectVersionId ?? '' );

                // ensure that the project versionID inside local storage from the prefs exists, otherwise go back to the default project version ( down below )
                if( !project.isCorvalManaged && ( !projectVersionId || projectVersionNotFound ) ){

                    const defaultVersion = ProjectService.getDefaultProjectVersion( project.projectVersions );

                    if( defaultVersion ){

                        projectVersionId = defaultVersion.id;
                    }
                }

                // remove any existing project version ID if the project is Corval managed
                if( project.isCorvalManaged && projectVersionId ){

                    projectVersionId = null;
                }

                // Assess if this project is a template source based on the current project version ID
                const sourceProjectVersionIds = ( project.isTemplateSource ) ? _.pluck( getProjectTemplatesReq.data?.me.projectTemplates, 'sourceProjectVersionId' ) : [],
                      isTemplateSource        = sourceProjectVersionIds.includes( projectVersionId );

                // Business Functions
                const businessFunctions                                      = project.businessFunctions,
                      [ archivedBusinessFunctions, activeBusinessFunctions ] = _.partition( businessFunctions, b => b.isArchived );

                // Construct Config
                //  - projectUtilSettings is a placeholder; can be expanded in the future to include more specific settings as needed
                //  - projectDictionary is the term dictionary specific to this project
                //  - projectVersionId is the current project version in use ( null if Corval managed )
                //  - isCorvalManaged and isDiscoveryComplete are flags that impact behavior in various places
                //  - isTemplateSource indicates if this project version is being used as a template source
                const { isCorvalManaged, isDiscoveryComplete, termDictionary } = project,
                      projectDictionary: ITermDictionary                       = JSON.parse( termDictionary ?? EMPTY_PROJECT_CONFIG ),
                      projectBusinessFunctions                                 = {
                          all:      businessFunctions,
                          active:   activeBusinessFunctions,
                          archived: archivedBusinessFunctions,
                      },
                      projectUtilSettings                                      = {},
                      config                                                   = {
                          projectDictionary,
                          projectBusinessFunctions,
                          projectUtilSettings,
                          isCorvalManaged,
                          isDiscoveryComplete,
                          projectVersionId,
                          isTemplateSource,
                      };

                setConfig( config );
            }
        }

    }, [ getProjectConfigReq, getProjectTemplatesReq ] );

    // When config gets updated...
    useEffect( () => {

        runIfFunction( props.onConfigUpdated, config );

    }, [ config ] );

    // When we have a config, continue on
    return ( config ) ? <>{ props.children }</> : null;
};

ProjectConfig.defaultProps = defaultProps;

export { ProjectConfig };
